import { Fragment } from 'react';
import Media from 'react-media';
import ReactMarkdown from 'react-markdown';

import { useTheme, Column } from '@simplywallst/ui-core';

import {
  TeamReviewsContainerRow,
  TeamReviewsHeader,
  StyledReactMarkdown,
  TeamReviewsContentRow,
  ImageContainerMobile,
  ImageContainer,
} from './styled';
import { OneColumnExpanded } from '../../styled';
import Carousel, { Slides, Slide } from '@components/Carousel';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { useContentfulImage } from '@/hooks/useContentfulImage';
import DeferRenderUntilIntersecting from '@/components/DeferRenderUntilIntersecting';
import { Helmet } from 'react-helmet-async';

interface Image {
  title?: string;
  description?: string;
  url?: string;
}

interface Props {
  title?: string;
  markdownContent: string[];
  gallery?: Image[];
}

export const TeamReviews = ({ title, markdownContent, gallery }: Props) => {
  const theme = useTheme();
  const { isMobile, isTablet } = useUserAgent();
  const reviewImages = [
    {
      avif: useContentfulImage({
        url: gallery && gallery[0].url,
        title: gallery && gallery[0].title,
        format: 'avif',
      }),
      jpg: useContentfulImage({
        url: gallery && gallery[0].url,
        title: gallery && gallery[0].title,
      }),
    },
    {
      avif: useContentfulImage({
        url: gallery && gallery[1].url,
        title: gallery && gallery[1].title,
        format: 'avif',
      }),
      jpg: useContentfulImage({
        url: gallery && gallery[1].url,
        title: gallery && gallery[1].title,
      }),
    },
    {
      avif: useContentfulImage({
        url: gallery && gallery[2].url,
        title: gallery && gallery[2].title,
        format: 'avif',
      }),
      jpg: useContentfulImage({
        url: gallery && gallery[2].url,
        title: gallery && gallery[2].title,
      }),
    },
  ];

  return (
    <>
      <Helmet>
        {reviewImages.map((item, i) => (
          <Fragment key={i}>
            <link rel="prefetch" href={item.avif?.src} />
            <link rel="prefetch" href={item.jpg?.src} />
          </Fragment>
        ))}
      </Helmet>
      <TeamReviewsContainerRow justifyContent="center">
        <OneColumnExpanded>
          <TeamReviewsHeader data-cy-id="team">
            <h2>{title}</h2>
          </TeamReviewsHeader>

          <TeamReviewsContentRow alignItem="flex-start" noWrap>
            {markdownContent.length > 0 && (
              <Media
                defaultMatches={isMobile || isTablet}
                query={{ maxWidth: theme.mdMaxRaw }}
              >
                {(match) => {
                  if (match) {
                    return (
                      <Carousel
                        showDots
                        slidesToScroll={1}
                        totalSlides={markdownContent.length}
                      >
                        <Slides>
                          {markdownContent
                            ?.filter((review) => review !== '')
                            .map((review, i) => (
                              <Slide key={i} itemsToShow={1}>
                                <Column>
                                  <StyledReactMarkdown key={i}>
                                    <ReactMarkdown source={review} />
                                  </StyledReactMarkdown>
                                  {gallery?.[i] && (
                                    <ImageContainerMobile>
                                      <DeferRenderUntilIntersecting>
                                        {() => (
                                          <picture>
                                            <source
                                              srcSet={reviewImages[i].avif?.src}
                                              type="image/avif"
                                            />
                                            <img
                                              src={reviewImages[i].jpg?.src}
                                              alt={reviewImages[i].jpg?.alt}
                                            />
                                          </picture>
                                        )}
                                      </DeferRenderUntilIntersecting>

                                      <div>
                                        <strong>{gallery[i].title}</strong>
                                        <p>{gallery[i].description}</p>
                                      </div>
                                    </ImageContainerMobile>
                                  )}
                                </Column>
                              </Slide>
                            ))}
                        </Slides>
                      </Carousel>
                    );
                  }
                  return (
                    <>
                      {markdownContent
                        ?.filter((review) => review !== '')
                        .map((review, i) => (
                          <Column key={i} colMd={4}>
                            <StyledReactMarkdown key={i}>
                              <ReactMarkdown source={review} />
                            </StyledReactMarkdown>
                            {gallery?.[i] && (
                              <ImageContainer>
                                <DeferRenderUntilIntersecting>
                                  {() => (
                                    <picture>
                                      <source
                                        srcSet={reviewImages[i].avif?.src}
                                        type="image/avif"
                                      />
                                      <img
                                        src={reviewImages[i].jpg?.src}
                                        alt={reviewImages[i].jpg?.alt}
                                      />
                                    </picture>
                                  )}
                                </DeferRenderUntilIntersecting>
                                <strong>{gallery[i].title}</strong>
                                <p>{gallery[i].description}</p>
                              </ImageContainer>
                            )}
                          </Column>
                        ))}
                    </>
                  );
                }}
              </Media>
            )}
          </TeamReviewsContentRow>
        </OneColumnExpanded>
      </TeamReviewsContainerRow>
    </>
  );
};
